import React, { useState, useMemo } from 'react';
import { 
  IoAdd, 
  IoList, 
  IoCalendar, 
  IoChevronDown, 
  IoChevronBack, 
  IoChevronForward,
  IoSchoolOutline,
  IoCard,
  IoStar,
  IoTime,
  IoCalendarOutline,
  IoDocument,
  IoPencil,
  IoClose,
  IoCheckmark
} from 'react-icons/io5';
import { User } from '@/types';
import { useDeadlinesData } from './hooks/useDeadlinesData';
import ListView from './components/ListView';
import CalendarView from './components/CalendarView';
import AddDeadlineModal from './components/AddDeadlineModal';
import EditDeadlineModal from './components/EditDeadlineModal';
import './Deadlines.css';

export type DeadlineType = 'application' | 'scholarship' | 'opportunity';

export interface Deadline {
  id: string;
  name: string;
  type: DeadlineType;
  description: string;
  dueDate: string;
  dueTime: string;
  status: 'completed' | 'in_progress' | 'not_started';
  progress: number;
  estimatedTimeHours: number;
  priority: 'high' | 'medium' | 'low';
  daysLeft: number;
}

export type ViewType = 'list' | 'calendar';
export type FilterType = 'all' | 'this_week' | 'this_month' | 'overdue';

interface DeadlinesProps {
  user?: User | null;
}

const Deadlines: React.FC<DeadlinesProps> = ({ user }) => {
  const [viewType, setViewType] = useState<ViewType>('list');
  const [activeFilters, setActiveFilters] = useState<Set<DeadlineType>>(
    new Set(['application', 'scholarship', 'opportunity'])
  );
  const [timeFilter, setTimeFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<'date_earliest' | 'date_latest' | 'priority' | 'school_name'>('date_earliest');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState<Deadline | null>(null);

  const { deadlines, isLoading, error } = useDeadlinesData();

  // Filter and sort deadlines
  const filteredDeadlines = useMemo(() => {
    return deadlines
      .filter(deadline => activeFilters.has(deadline.type))
      .filter(deadline => {
        switch (timeFilter) {
          case 'this_week':
            return deadline.daysLeft <= 7;
          case 'this_month':
            return deadline.daysLeft <= 30;
          case 'overdue':
            return deadline.daysLeft < 0;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'date_latest':
            return b.daysLeft - a.daysLeft;
          case 'priority':
            const priorityOrder = { high: 3, medium: 2, low: 1 };
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          case 'school_name':
            return a.name.localeCompare(b.name);
          default: // date_earliest
            return a.daysLeft - b.daysLeft;
        }
      });
  }, [deadlines, activeFilters, timeFilter, sortBy]);

  // Group deadlines by date for list view
  const groupedDeadlines = useMemo(() => {
    const groups: Record<string, Deadline[]> = {};
    filteredDeadlines.forEach(deadline => {
      const date = new Date(deadline.dueDate).toDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(deadline);
    });
    return groups;
  }, [filteredDeadlines]);

  const handleFilterChange = (type: DeadlineType) => {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(type)) {
      newFilters.delete(type);
    } else {
      newFilters.add(type);
    }
    setActiveFilters(newFilters);
  };

  const handleEditDeadline = (deadline: Deadline) => {
    setSelectedDeadline(deadline);
    setShowEditModal(true);
  };

  const getTypeIcon = (type: DeadlineType) => {
    switch (type) {
      case 'application':
        return <IoSchoolOutline />;
      case 'scholarship':
        return <IoCard />;
      case 'opportunity':
        return <IoStar />;
    }
  };

  const getTypeColor = (type: DeadlineType) => {
    switch (type) {
      case 'application':
        return 'blue';
      case 'scholarship':
        return 'green';
      case 'opportunity':
        return 'purple';
    }
  };

  if (isLoading) {
    return (
      <div className="deadlines-page">
        <div className="deadlines-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading deadlines...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="deadlines-page">
        <div className="deadlines-container">
          <div className="error-state">
            <h2>Error loading deadlines</h2>
            <p>Please try refreshing the page or contact support if the problem persists.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="deadlines-page">
      <div className="deadlines-container">
        {/* Header */}
        <div className="deadlines-header">
          <h1 className="deadlines-title">Deadlines</h1>
          <button 
            className="add-deadline-btn"
            onClick={() => setShowAddModal(true)}
          >
            <IoAdd /> Add Deadline
          </button>
        </div>

        {/* View Toggle and Filters */}
        <div className="deadlines-controls">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewType === 'list' ? 'active' : ''}`}
              onClick={() => setViewType('list')}
            >
              <IoList /> List View
            </button>
            <button
              className={`view-toggle-btn ${viewType === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewType('calendar')}
            >
              <IoCalendar /> Calendar View
            </button>
          </div>

          <div className="deadline-filters">
            {(['application', 'scholarship', 'opportunity'] as DeadlineType[]).map(type => (
              <label key={type} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={activeFilters.has(type)}
                  onChange={() => handleFilterChange(type)}
                />
                <span className={`filter-indicator filter-${getTypeColor(type)}`}></span>
                <span className="filter-label">
                  {type === 'application' && 'Application Deadlines'}
                  {type === 'scholarship' && 'Scholarship Deadlines'}
                  {type === 'opportunity' && 'Important Opportunities'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="deadlines-content">
          {viewType === 'list' ? (
            <ListView
              deadlines={filteredDeadlines}
              groupedDeadlines={groupedDeadlines}
              timeFilter={timeFilter}
              setTimeFilter={setTimeFilter}
              sortBy={sortBy}
              setSortBy={setSortBy}
              onEditDeadline={handleEditDeadline}
              getTypeIcon={getTypeIcon}
              getTypeColor={getTypeColor}
            />
          ) : (
            <CalendarView
              deadlines={filteredDeadlines}
              onEditDeadline={handleEditDeadline}
              getTypeIcon={getTypeIcon}
              getTypeColor={getTypeColor}
            />
          )}
        </div>

        {/* Modals */}
        {showAddModal && (
          <AddDeadlineModal
            onClose={() => setShowAddModal(false)}
            getTypeIcon={getTypeIcon}
            getTypeColor={getTypeColor}
          />
        )}

        {showEditModal && selectedDeadline && (
          <EditDeadlineModal
            deadline={selectedDeadline}
            onClose={() => {
              setShowEditModal(false);
              setSelectedDeadline(null);
            }}
            getTypeIcon={getTypeIcon}
            getTypeColor={getTypeColor}
          />
        )}
      </div>
    </div>
  );
};

export default Deadlines;